import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { selectToken } from '../../../util/tokenSelector.js';
import { Input } from '../Input/Input.js';
import { useExperimentContext } from '../../../contexts/session/Experiment.js';
import { useThemeContext } from '../../../contexts/session/Theme.js';
import { CheckboxStyled } from './Checkbox.styled.js';

var _excluded = ["state", "selection", "children"];
var checkmark = function checkmark(fill) {
  return /*#__PURE__*/React.createElement("svg", {
    className: "ads-non-interactive",
    xmlns: "http://www.w3.org/2000/svg",
    "aria-hidden": "true",
    width: "16",
    height: "16",
    viewBox: "0 0 24 24",
    fill: fill
  }, /*#__PURE__*/React.createElement("path", {
    className: "ads-non-interactive",
    d: "m21.453 4.487 1.094 1.026a.5.5 0 0 1 .023.707L10.412 19.188a1.25 1.25 0 0 1-1.692.122l-.104-.093-7.146-7.146a.5.5 0 0 1 0-.707l1.06-1.061a.5.5 0 0 1 .707 0l6.234 6.234L20.746 4.51a.5.5 0 0 1 .707-.023Z"
  }));
};
var minus = function minus(fill) {
  return /*#__PURE__*/React.createElement("svg", {
    className: "ads-non-interactive",
    xmlns: "http://www.w3.org/2000/svg",
    "aria-hidden": "true",
    width: "16",
    height: "16",
    viewBox: "0 0 24 24",
    fill: fill
  }, /*#__PURE__*/React.createElement("path", {
    className: "ads-non-interactive",
    d: "M3.364 10.5h17.272c.753 0 1.364.672 1.364 1.5s-.61 1.5-1.364 1.5H3.364C2.61 13.5 2 12.828 2 12s.61-1.5 1.364-1.5Z"
  }));
};
var getBackgroundColor = function getBackgroundColor(_ref) {
  var checked = _ref.checked,
    indeterminate = _ref.indeterminate,
    disabled = _ref.disabled,
    radioCheckboxA11yContrastFix = _ref.radioCheckboxA11yContrastFix;
  if (disabled) {
    var _backgroundColor = radioCheckboxA11yContrastFix ? 'state.disabled' : 'deprecated.neutral.disabled-2';
    return checked || indeterminate ? _backgroundColor : undefined;
  }
  var backgroundColor = radioCheckboxA11yContrastFix ? 'state.active' : 'product.primary';
  return checked || indeterminate ? backgroundColor : 'base.surface';
};
var getIconFillColor = function getIconFillColor(_ref2) {
  var theme = _ref2.theme,
    disabled = _ref2.disabled,
    radioCheckboxA11yContrastFix = _ref2.radioCheckboxA11yContrastFix;
  if (radioCheckboxA11yContrastFix) {
    return disabled ? selectToken(theme.text, 'state.disabled') : selectToken(theme.text, 'base.inverted');
  }
  return '#fff';
};

/**
 * Checkboxes allow users to select a single or multiple options from a list of options.
 */
var Checkbox = /*#__PURE__*/React.forwardRef(function (_ref3, ref) {
  var state = _ref3.state,
    selection = _ref3.selection,
    children = _ref3.children,
    rest = _objectWithoutProperties(_ref3, _excluded);
  var theme = useThemeContext();
  var _useExperimentContext = useExperimentContext(),
    radioCheckboxA11yContrastFix = _useExperimentContext.radioCheckboxA11yContrastFix;
  var disabled = state === 'disabled';
  var checked = selection === 'selected';
  var indeterminate = selection === 'indeterminate';
  var backgroundColor = getBackgroundColor({
    checked: checked,
    indeterminate: indeterminate,
    disabled: disabled,
    radioCheckboxA11yContrastFix: radioCheckboxA11yContrastFix
  });
  var iconFillColor = getIconFillColor({
    theme: theme,
    disabled: disabled,
    radioCheckboxA11yContrastFix: radioCheckboxA11yContrastFix
  });
  var showCheckmark = checked && !indeterminate;
  var borderColor = radioCheckboxA11yContrastFix ? 'state.inactive' : 'base.default';
  var disabledBorderColor = radioCheckboxA11yContrastFix ? 'state.disabled' : 'base.subtle';
  var checkbox = /*#__PURE__*/React.createElement(CheckboxStyled, {
    borderStyle: "solid",
    borderRadius: "2",
    borderColor: disabled ? disabledBorderColor : borderColor,
    borderWidth: checked || indeterminate ? '0' : '2',
    backgroundColor: backgroundColor
  }, showCheckmark && checkmark(iconFillColor), indeterminate && minus(iconFillColor));
  var typography = selectToken(theme.typography, 'body-s');
  var marginTop = typography ? "calc((".concat(typography.lineHeight, " - ").concat(typography.fontSize, ") / 2)") : '2px';
  return /*#__PURE__*/React.createElement(Input, _extends({
    inputType: "checkbox",
    selection: selection,
    state: state,
    control: {
      component: checkbox,
      styled: CheckboxStyled,
      size: '20px'
    }
  }, rest, {
    ref: ref
  }), children &&
  /*#__PURE__*/
  // TODO: ADSWEB-1082 Hardcoded div to add margin to the label children, should not be needed when align to 2.0.1
  React.createElement("div", {
    style: {
      marginTop: marginTop,
      display: 'flex',
      flexDirection: 'row'
    }
  }, children));
});
Checkbox.displayName = 'Checkbox';

export { Checkbox };
//# sourceMappingURL=Checkbox.js.map
